<template>
  <div
    class="sidd-typing-indicator"
    :class="`sidd-typing-indicator--${props.size}`"
  >
    <div class="sidd-typing-indicator__content">
      <div class="sidd-typing-indicator__dots">
        <div
          class="sidd-typing-indicator__dot sidd-typing-indicator__dot--1"
        ></div>
        <div
          class="sidd-typing-indicator__dot sidd-typing-indicator__dot--2"
        ></div>
        <div
          class="sidd-typing-indicator__dot sidd-typing-indicator__dot--3"
        ></div>
      </div>
      <span v-if="props.showText" class="sidd-typing-indicator__text">
        {{ props.text }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ITypingIndicatorProps } from './types'

const props = withDefaults(defineProps<ITypingIndicatorProps>(), {
  showText: false,
  text: 'Sidd is typing...',
  size: 'medium',
})
</script>

<style>
.sidd-typing-indicator {
  display: flex;
  align-items: center;
  margin: 8px 0;
  align-self: flex-start;
  justify-content: flex-start;
  margin-right: auto;
  max-width: 75%;
  animation: slideIn 0.3s ease-out;
}

.sidd-typing-indicator__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  background: linear-gradient(
    135deg,
    hsl(var(--primary) / 0.03) 0%,
    hsl(var(--primary) / 0.05) 25%,
    rgba(255, 255, 255, 0.95) 50%,
    hsl(var(--primary) / 0.04) 75%,
    hsl(var(--primary) / 0.02) 100%
  );
  border: 1px solid hsl(var(--primary) / 0.08);
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  min-width: 60px;
  box-shadow: 0 2px 8px hsl(var(--primary) / 0.1);
}

.sidd-typing-indicator__dots {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
}

.sidd-typing-indicator__dot {
  width: 8px;
  height: 8px;
  background: hsl(var(--primary) / 0.7);
  border-radius: 50%;
  animation: typingPulse 1.5s infinite ease-in-out;
  box-shadow: 0 1px 3px hsl(var(--primary) / 0.2);
}

.sidd-typing-indicator__dot--1 {
  animation-delay: 0ms;
}

.sidd-typing-indicator__dot--2 {
  animation-delay: 300ms;
}

.sidd-typing-indicator__dot--3 {
  animation-delay: 600ms;
}

.sidd-typing-indicator__text {
  font-size: 0.75em;
  color: hsl(var(--primary));
  font-weight: 500;
  margin-left: 4px;
}

@keyframes typingPulse {
  0%,
  70%,
  100% {
    transform: scale(1);
    opacity: 0.6;
    background: hsl(var(--primary) / 0.7);
  }
  35% {
    transform: scale(1.3);
    opacity: 1;
    background: hsl(var(--primary) / 0.9);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.sidd-typing-indicator--small .sidd-typing-indicator__content {
  padding: 10px 12px;
}

.sidd-typing-indicator--small .sidd-typing-indicator__dot {
  width: 6px;
  height: 6px;
}

.sidd-typing-indicator--small .sidd-typing-indicator__text {
  font-size: 0.6875em;
}

.sidd-typing-indicator--large .sidd-typing-indicator__content {
  padding: 16px 20px;
}

.sidd-typing-indicator--large .sidd-typing-indicator__dot {
  width: 10px;
  height: 10px;
}

.sidd-typing-indicator--large .sidd-typing-indicator__text {
  font-size: 0.8125em;
}

@media (prefers-reduced-motion: reduce) {
  .sidd-typing-indicator {
    animation: none;
  }

  .sidd-typing-indicator__dot {
    animation: none;
    opacity: 0.8;
    transform: scale(1);
  }
}
</style>
